import {
	Avatar,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	IconButton,
	Link,
	SwipeableDrawer,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { CheckCircle, Close } from '@mui/icons-material';
import { useEffect, useRef } from 'react';
import LoginOtpGroupedField from '../Login/LoginOtpGroupedField';
import { capitalize } from '../../hepler/functions';
import { useTranslation } from '../../hooks/useTranslation';
import stylesLogo from '../Accounts/Account.styles';
import Footer from '../Sections/Footer';
import { getFipLogoAlt } from '../../utils/fipLogo.utils';
import { getEntityThemedCtaButtonSx } from '../../utils/ctaContrast.utils';
import { handleOtpPaste } from '../../utils/a11y.utils';

const formatMaskedPhone = (phone) => {
	if (!phone) return '';
	const digits = String(phone).replace(/\D/g, '');
	const last4 = digits.slice(-4);
	return last4 ? `+91 ******${last4}` : '';
};

const getLinkAccountButtonSx = (customDetails, theme, isDark, flex) =>
	getEntityThemedCtaButtonSx(customDetails, isDark, {
		...(flex != null ? { flex } : {}),
		textTransform: 'none',
	});

/**
 * Shared content rendered inside both the mobile drawer and desktop dialog.
 * Shows OTP entry or success state depending on linkedStatus.
 */
function ModalContent({
	fipItem,
	otp,
	onOtpChange,
	isResend,
	time,
	isSubmitting,
	linkedStatus,
	onResend,
	onSubmit,
	onClose,
	customDetails,
	isDark,
	theme,
	t,
	isMobile,
	shouldFocusOtp,
}) {
	const secondaryTextColor =
		customDetails?.secondaryTextColor ?? theme.palette.text.secondary;
	const fallbackOtpRef = useRef(null);
	const parsedLength =
		fipItem?.linkingOtpLength &&
		Number.isInteger(Number(fipItem.linkingOtpLength))
			? Number(fipItem.linkingOtpLength)
			: null;
	const fipDisplayName = t(`fips.${fipItem?.fipId}`);
	const fipLogoAlt = getFipLogoAlt(fipItem?.fipId, fipDisplayName);

	useEffect(() => {
		if (!shouldFocusOtp || linkedStatus !== null || parsedLength) return undefined;
		const timer = setTimeout(() => fallbackOtpRef.current?.focus(), 50);
		return () => clearTimeout(timer);
	}, [shouldFocusOtp, linkedStatus, fipItem?.fipId, parsedLength]);

	if (linkedStatus !== null) {
		// Success state
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: '1rem',
					py: isMobile ? '2.5rem' : '1.5rem',
					px: '1rem',
				}}
			>
				<Box sx={{ position: 'relative', display: 'inline-flex' }}>
					<Avatar
						alt={fipLogoAlt}
						src={global.objectFormatFip[fipItem?.fipId]?.productIconUri}
						sx={{
							...stylesLogo.avatarStyle,
							width: 64,
							height: 64,
							background: theme.palette.primary.light,
						}}
					/>
					<CheckCircle
						sx={{
							position: 'absolute',
							bottom: -4,
							right: -4,
							fontSize: 26,
							color: '#3053D3',
							background: isDark ? '#1E1E1E' : '#fff',
							borderRadius: '50%',
						}}
					/>
				</Box>
				<Typography
					fontWeight={700}
					fontSize="1.125rem"
					textAlign="center"
					color={isDark ? '#FFFFFF' : '#000000'}
				>
					Successfully linked your {t(`fips.${fipItem?.fipId}`)}{' '}
					{fipItem?.accounts?.length === 1 ? 'account' : 'accounts'}
				</Typography>
				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: '0.5rem',
						justifyContent: 'center',
					}}
				>
					{fipItem?.accounts?.map((acc) => (
						<Chip
							key={acc.accRefNumber}
							label={`${capitalize(acc.accType)} \u2022 ${acc.maskedAccNumber?.slice(-4)}`}
							size="small"
							sx={{
								fontSize: '0.875rem',
								backgroundColor: isDark ? '#3D3D3D' : '#F5F5F5',
								color: secondaryTextColor,
							}}
						/>
					))}
				</Box>
			</Box>
		);
	}

	// OTP entry state
	const isLinkDisabled =
		!otp ||
		(parsedLength
			? otp.length !== parsedLength
			: otp.length !== 4 && otp.length !== 6 && otp.length !== 8) ||
		isSubmitting;

	const handleFormSubmit = (event) => {
		event.preventDefault();
		if (!isLinkDisabled) {
			onSubmit();
		}
	};

	return (
		<Box
			component="form"
			id="linking-otp-form"
			noValidate
			onSubmit={handleFormSubmit}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '1rem',
				p: isMobile ? '1.5rem 1rem' : 0,
			}}
		>
			{/* FIP header — logo, then title, then OTP subtitle (stacked) */}
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
				<Avatar
					alt={fipLogoAlt}
					src={global.objectFormatFip[fipItem?.fipId]?.productIconUri}
					sx={{
						...stylesLogo.avatarStyle,
						width: 40,
						height: 40,
						alignSelf: 'flex-start',
						background: theme.palette.primary.light,
					}}
				/>
				<Typography
					fontWeight={700}
					fontSize="1.125rem"
					color={isDark ? '#FFFFFF' : '#000000'}
					sx={{ lineHeight: 1.3 }}
				>
					Link your {t(`fips.${fipItem?.fipId}`)} Accounts
				</Typography>
				<Typography fontSize="0.875rem" color={secondaryTextColor}>
					{t('accountDiscovery.messages.enterOtpSentTo')}{' '}
					{formatMaskedPhone(fipItem?.phoneNumber)}
				</Typography>
			</Box>

			{/* Account chips */}
			<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
				{fipItem?.accounts?.map((acc) => (
					<Chip
						key={acc.accRefNumber}
						label={`${capitalize(acc.accType)} \u2022 ${acc.maskedAccNumber?.slice(-4)}`}
						size="small"
						sx={{
							fontSize: '0.875rem',
							backgroundColor: isDark ? '#3D3D3D' : '#F5F5F5',
							color: secondaryTextColor,
						}}
					/>
				))}
			</Box>

			{/* OTP input — boxes if linkingOtpLength is set, else single TextField */}
			{parsedLength ? (
				<LoginOtpGroupedField
					value={otp}
					onChange={onOtpChange}
					length={parsedLength}
					autoFocus={shouldFocusOtp}
					focusKey={fipItem?.fipId}
					primaryColor={
						customDetails?.primaryColor || theme.palette.primary.main
					}
					customDetails={customDetails}
				/>
			) : (
				<TextField
					label={t('common.labels.otp')}
					size="small"
					value={otp}
					placeholder={t('common.labels.enterOtp')}
					autoComplete="one-time-code"
					inputProps={{
						style: { fontSize: '0.875rem' },
						autoComplete: 'one-time-code',
						inputMode: 'numeric',
					}}
					inputRef={fallbackOtpRef}
					variant="outlined"
					onChange={(e) => onOtpChange(e.target.value)}
					onPaste={handleOtpPaste(onOtpChange, 8)}
					sx={{ width: '65%' }}
				/>
			)}

			{/* Resend */}
			<Typography fontSize="0.875rem" color={secondaryTextColor}>
				{t('accountDiscovery.messages.didNotReceiveOtp')}{' '}
				{!isResend ? (
					<>
						{t('accountDiscovery.messages.tryAgain')} in{' '}
						<strong style={{ color: theme.palette.error.main }}>
							{time} sec
						</strong>
					</>
				) : (
					<Link
						component="button"
						underline="none"
						onClick={onResend}
						sx={{
							cursor: 'pointer',
							fontSize: '0.875rem',
							fontWeight: 600,
							color: customDetails?.primaryColor || theme.palette.primary.main,
							verticalAlign: 'baseline',
							p: 0,
							border: 'none',
							background: 'none',
						}}
					>
						{t('accountDiscovery.messages.tryAgain')}
					</Link>
				)}
			</Typography>

			{/* Skip + Link buttons — mobile drawer only (desktop uses DialogActions) */}
			{isMobile && (
				<Box sx={{ display: 'flex', gap: '0.75rem', mt: '0.5rem' }}>
					<Button
						variant="outlined"
						type="button"
						onClick={onClose}
						sx={{
							flex: 1,
							textTransform: 'none',
							backgroundColor: 'transparent !important',
							color: isDark
								? '#FFFFFF !important'
								: `${customDetails?.primaryColor || theme.palette.primary.main} !important`,
							borderColor: isDark
								? '#555 !important'
								: `${customDetails?.primaryColor || theme.palette.primary.main} !important`,
						}}
					>
						{t('common.buttons.skip')}
					</Button>
					<Button
						variant="contained"
						type="submit"
						disabled={isLinkDisabled}
						sx={getLinkAccountButtonSx(customDetails, theme, isDark, 2)}
					>
						{isSubmitting ? (
							<CircularProgress size={22} sx={{ color: '#fff' }} />
						) : (
							t('accountDiscovery.messages.linkAccountsButton')
						)}
					</Button>
				</Box>
			)}
		</Box>
	);
}

/**
 * Responsive linking OTP modal.
 * Mobile → bottom SwipeableDrawer
 * Desktop → centered Dialog
 */
export default function LinkingOtpModal({
	open,
	onClose,
	fipItem,
	otp,
	onOtpChange,
	isResend,
	time,
	isSubmitting,
	linkedStatus,
	onResend,
	onSubmit,
	customDetails,
}) {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isDark =
		customDetails?.darkTheme === true || theme.palette.mode === 'dark';

	const parsedOtpLength =
		fipItem?.linkingOtpLength &&
		Number.isInteger(Number(fipItem.linkingOtpLength))
			? Number(fipItem.linkingOtpLength)
			: null;

	const isLinkDisabled =
		!otp ||
		(parsedOtpLength
			? otp.length !== parsedOtpLength
			: otp.length !== 4 && otp.length !== 6 && otp.length !== 8) ||
		isSubmitting;

	const shouldFocusOtp = open && linkedStatus === null;

	const sharedContent = (
		<ModalContent
			fipItem={fipItem}
			otp={otp}
			onOtpChange={onOtpChange}
			isResend={isResend}
			time={time}
			isSubmitting={isSubmitting}
			linkedStatus={linkedStatus}
			onResend={onResend}
			onSubmit={onSubmit}
			onClose={onClose}
			customDetails={customDetails}
			isDark={isDark}
			theme={theme}
			t={t}
			isMobile={isMobile}
			shouldFocusOtp={shouldFocusOtp}
		/>
	);

	if (isMobile) {
		return (
			<SwipeableDrawer
				anchor="bottom"
				open={open}
				onOpen={() => {}}
				onClose={linkedStatus !== null ? undefined : onClose}
				disableSwipeToOpen
				disableEscapeKeyDown={false}
				ModalProps={{ disableAutoFocus: true }}
				PaperProps={{
					sx: {
						borderRadius: '1rem 1rem 0 0',
						backgroundColor: isDark ? '#1E1E1E' : '#FFFFFF',
					},
				}}
			>
				{sharedContent}

				{/* Footer always visible inside drawer */}
				<Footer customDetails={customDetails} />
			</SwipeableDrawer>
		);
	}

	return (
		<Dialog
			open={open}
			onClose={linkedStatus !== null ? undefined : onClose}
			disableEscapeKeyDown={false}
			disableAutoFocus
			maxWidth="xs"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: '1rem',
					backgroundColor: isDark ? '#1E1E1E' : '#FFFFFF',
					color: isDark ? '#FFFFFF' : '#000000',
				},
			}}
		>
			{/* Close button — only shown when OTP entry (not success) */}
			{linkedStatus === null && (
				<IconButton
					onClick={onClose}
					aria-label={t('common.buttons.close')}
					sx={{
						position: 'absolute',
						top: '0.75rem',
						right: '0.75rem',
						color: isDark ? '#FFFFFF' : theme.palette.primary.main,
					}}
				>
					<Close />
				</IconButton>
			)}

			<DialogContent sx={{ pt: '1.5rem', pb: '1rem' }}>
				{sharedContent}
			</DialogContent>

			{linkedStatus === null && (
				<>
					<Divider />
					<DialogActions sx={{ px: '1.5rem', py: '1rem', gap: '0.5rem' }}>
						<Button
							variant="outlined"
							type="button"
							onClick={onClose}
							sx={{
								textTransform: 'none',
								backgroundColor: 'transparent !important',
								color: isDark
									? '#FFFFFF !important'
									: `${customDetails?.primaryColor || theme.palette.primary.main} !important`,
								borderColor: isDark
									? '#555 !important'
									: `${customDetails?.primaryColor || theme.palette.primary.main} !important`,
							}}
						>
							{t('common.buttons.skip')}
						</Button>
						<Button
							variant="contained"
							type="submit"
							form="linking-otp-form"
							disabled={isLinkDisabled}
							sx={getLinkAccountButtonSx(customDetails, theme, isDark)}
						>
							{isSubmitting ? (
								<CircularProgress size={20} sx={{ color: '#fff' }} />
							) : (
								t('accountDiscovery.messages.linkAccountsButton')
							)}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
